import { useMutation, useQuery } from '@apollo/client';
import {
  FileAttachmentChange,
  FileAttachmentInputWithClassification,
  FileAttachmentStatus,
  FileAttachmentsWidget,
  FileAttachmentsWidgetEditFeature,
} from '@procureai/ui-modules-mui';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgAttachmentMutationAction } from '@/generated/gql/graphql';
import { I18nNamespace } from '@/legacy/common/types';
import { ChatMessageProps } from '@/legacy/common/types/ChatMessage';
import { ChatActionNext } from '@/legacy/components/ChatStep/ChatActions/ChatActionNext';
import { ChatStep } from '@/legacy/components/ChatStep/ChatStep';
import { useChatSession } from '@/legacy/modules';

import {
  INTAKE_ATTACHMENTS_UPDATE_MUTATION,
  INTAKE_ATTACHMENTS_NEXT_MUTATION,
} from './IntakeAttachmentsStep.mutation.gql';
import { INTAKE_ATTACHMENTS_QUERY } from './IntakeAttachmentsStep.query.gql';

export const IntakeAttachmentsStep = ({ role, createdBy, chatSessionCompleted }: ChatMessageProps): ReactNode => {
  const { t: tIntake } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeAttachmentsStep' });
  const { onError } = useCustomSnackbar();
  const session = useChatSession();

  const { data, loading, updateQuery } = useQuery(INTAKE_ATTACHMENTS_QUERY, {
    variables: { id: session.id },
  });

  const [updateAttachments, { loading: saving }] = useMutation(INTAKE_ATTACHMENTS_UPDATE_MUTATION, {
    update: (_cache, { data: mutationData }) => {
      if (!mutationData?.sevenbridges.chatSession?.attachment.update) {
        return;
      }

      const updatedAttachments = mutationData.sevenbridges.chatSession.attachment.update;

      updateQuery((prevData) => {
        if (!prevData?.sevenbridges.chatSession) {
          return prevData;
        }

        return {
          ...prevData,
          sevenbridges: {
            ...prevData.sevenbridges,
            chatSession: {
              ...prevData.sevenbridges.chatSession,
              attachments: updatedAttachments,
            },
          },
        };
      });
    },
  });

  const [requestNext, { loading: isRequestingNext }] = useMutation(INTAKE_ATTACHMENTS_NEXT_MUTATION, {
    variables: {
      data: {
        chatSessionId: session.id,
      },
    },
    onError,
  });

  // turn to FileAttachment types for widget
  const attachments: FileAttachmentInputWithClassification[] = useMemo(() => {
    if (!data?.sevenbridges.chatSession) {
      return [];
    }

    const { id, attachments } = data.sevenbridges.chatSession;

    return (
      attachments.map((attachment) => ({
        id: attachment.id,
        name: attachment.name,
        createdAt: attachment.createdAt,

        // visible self ref to parent chat session
        visibilities: attachment.isVisible ? [{ id }] : [],
        parent: { id },
      })) || []
    );
  }, [data?.sevenbridges.chatSession]);

  const onSave = useCallback(
    async (changes: FileAttachmentChange[]) => {
      await updateAttachments({
        variables: {
          data: {
            chatSessionId: session.id,
            attachments: changes.map((change) => ({
              ...change,
              // typecast needed for SvgbrgAttachmentMutationAction enum mismatch
              action: change.action as unknown as SvbrgAttachmentMutationAction,
            })),
          },
        },
        onError,
      });
    },
    [onError, session.id, updateAttachments],
  );

  return (
    <ChatStep
      title={tIntake('IntakeChatStep.assistant')}
      role={role}
      createdBy={createdBy}
      message={t('message')}
      footer={
        <ChatActionNext
          disabled={chatSessionCompleted || loading || saving || isRequestingNext}
          loading={isRequestingNext}
          onClick={() => void requestNext()}
        />
      }
    >
      <FileAttachmentsWidget<FileAttachmentInputWithClassification>
        loading={loading}
        isEditable={!chatSessionCompleted}
        attachments={attachments}
        saving={saving}
        title={t('widgetTitle')}
        onSave={onSave}
        getItemStatus={(a) =>
          a.visibilities.some((v) => v.id === a.parent.id) ? FileAttachmentStatus.VISIBLE : FileAttachmentStatus.HIDDEN
        }
        isItemDeletable={() => !chatSessionCompleted}
        editFeatures={[FileAttachmentsWidgetEditFeature.UPLOAD, FileAttachmentsWidgetEditFeature.CHANGE_VISIBILITY]}
      />
    </ChatStep>
  );
};
